import React from "react";
import { LayoutGrid, ChevronLeft, ChevronRight } from "lucide-react";
import UserMenu from "./UserMenu";
import ThemeToggle from "../../ThemeToggle";
import LanguageSwitcher from "../../LanguageSwitcher";
import { appConfig } from "../../../config/app.config";

interface MenuItem {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

export interface SidebarBrand {
  /** 图标背景渐变 Tailwind class，例如 "from-brand-500 to-brand-600" */
  gradient: string;
  /** 头部图标组件，默认 LayoutGrid */
  icon?: React.ComponentType<{ className?: string }>;
  /** 主标题 */
  name: string;
  /** 副标题 */
  description?: string;
  /** 点击 brand 区域触发，例如返回首页或退出工作区 */
  onClick?: () => void;
}

interface PcMenuProps {
  collapsed: boolean;
  menuItems: MenuItem[];
  selectedKey: string;
  userName?: string;
  userRole?: string;
  /** 自定义品牌区，留空则使用 appConfig 默认值 */
  brand?: SidebarBrand;
  onToggle: () => void;
  onMenuClick: (key: string) => void;
  onLogout: () => void;
  onProfileClick: () => void;
  onChangePasswordClick: () => void;
}

const PcMenu: React.FC<PcMenuProps> = ({
  collapsed,
  menuItems,
  selectedKey,
  userName,
  userRole,
  brand,
  onToggle,
  onMenuClick,
  onLogout,
  onProfileClick,
  onChangePasswordClick,
}) => {
  const resolvedBrand: SidebarBrand = brand ?? {
    gradient: appConfig.icon.gradient,
    icon: appConfig.icon.component,
    name: appConfig.name,
    description: appConfig.description,
  };
  const BrandIcon = resolvedBrand.icon ?? LayoutGrid;

  return (
    <aside
      className={`sidebar relative border-r border-border-subtle bg-bg-surface transition-all duration-300 ease-in-out ${collapsed ? "w-20" : "w-[244px]"}`}
    >
      <div className="h-full flex flex-col">
        <div className="flex-shrink-0 border-b border-border-subtle p-[18px]">
          <div
            className={`flex items-center gap-3 ${resolvedBrand.onClick ? "cursor-pointer" : ""}`}
            onClick={resolvedBrand.onClick}
          >
            <div
              className={`flex h-[38px] w-[38px] items-center justify-center rounded-[12px] bg-gradient-to-br ${resolvedBrand.gradient}`}
            >
              <BrandIcon className="h-[19px] w-[19px] text-white" />
            </div>
            {!collapsed && (
              <div className="flex min-w-0 flex-col gap-[1px]">
                <span className="truncate font-display text-[15px] font-semibold text-text-primary">
                  {resolvedBrand.name}
                </span>
                {resolvedBrand.description && (
                  <span className="truncate font-mono text-[10px] font-normal text-text-muted">
                    {resolvedBrand.description}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <nav className="menu-scrollbar flex-1 overflow-y-auto px-3 py-3">
          <ul className={collapsed ? "space-y-2" : "space-y-0.5"}>
            {menuItems.map((item) => {
              const IconComponent = item.icon;
              const active = selectedKey === item.key;
              return (
                <li key={item.key}>
                  <button
                    onClick={() => onMenuClick(item.key)}
                    className={`w-full cursor-pointer ${
                      collapsed
                        ? "flex flex-col items-center justify-center rounded-[8px] border border-transparent px-1 py-2.5 text-center transition-colors"
                        : "flex items-center gap-[11px] rounded-[8px] border border-transparent px-[10px] py-2 transition-colors"
                    } ${
                      active
                        ? "bg-primary-soft font-semibold text-primary-ink"
                        : "text-text-primary hover:bg-bg-elevated hover:text-primary-ink"
                    }`}
                  >
                    <IconComponent className={`h-4 w-4 shrink-0 ${active ? "text-primary-ink" : "text-text-muted"}`} />
                    {!collapsed ? (
                      <span className={`min-w-0 flex-1 truncate whitespace-nowrap text-left text-sm ${active ? "font-semibold" : "font-medium"}`}>{item.label}</span>
                    ) : (
                      <span className={`mt-1 max-w-full truncate whitespace-nowrap text-xs leading-tight ${active ? "font-semibold" : "font-medium"}`}>
                        {item.label}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="flex-shrink-0 border-t border-border-subtle p-3">
          {collapsed ? (
            <div className="flex flex-col items-center gap-0.5">
              <ThemeToggle compact />
              <LanguageSwitcher compact />
              <UserMenu
                userName={userName}
                userRole={userRole}
                onProfileClick={onProfileClick}
                onChangePasswordClick={onChangePasswordClick}
                onLogout={onLogout}
                collapsed
              />
            </div>
          ) : (
            <div className="flex items-center justify-between gap-1">
              <UserMenu
                userName={userName}
                userRole={userRole}
                onProfileClick={onProfileClick}
                onChangePasswordClick={onChangePasswordClick}
                onLogout={onLogout}
                collapsed={false}
              />
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <ThemeToggle compact />
                <LanguageSwitcher compact />
              </div>
            </div>
          )}
        </div>

        <button
          onClick={onToggle}
          className="absolute -right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full border border-border-subtle bg-bg-surface shadow-sm transition-colors hover:bg-bg-elevated"
        >
          {collapsed ? (
            <ChevronRight className="h-3 w-3 text-text-secondary" />
          ) : (
            <ChevronLeft className="h-3 w-3 text-text-secondary" />
          )}
        </button>
      </div>
    </aside>
  );
};

export default PcMenu;
